import { useMemo } from "react";
import { useAuthSelector } from "../store/auth";

const useAuth = () => {
  const { user } = useAuthSelector();
  const token = localStorage.getItem("accessToken");
  const isAuthenticated = !!token;

  // Permissions are the single source of truth — role NAMES gate nothing.
  // viewer/editor/admin are presets, and a custom group holding the same
  // permissions behaves identically.
  //
  // Prefer the `permissions` array from /api/v2/auth/me/; fall back to
  // is_staff/is_superuser only for cached sessions that predate the field.
  const permissions: string[] = useMemo(() => {
    if (!user) return [];
    if (Array.isArray(user.permissions) && user.permissions.length > 0) return user.permissions;
    // Legacy fallback: synthesise permissions from staff/superuser flags
    if (user.is_superuser)
      return [
        "create",
        "delete",
        "manage_restrictions",
        "manage_users",
        "manage_configuration",
        "manage_styling",
        "bulk_api_data_import",
        "read",
        "update"
      ];
    if (user.is_staff) return ["create", "read", "update"];
    return ["read"];
  }, [user]);

  const canCreate = useMemo(() => permissions.includes("create"), [permissions]);
  const canUpdate = useMemo(() => permissions.includes("update"), [permissions]);
  const canDelete = useMemo(() => permissions.includes("delete"), [permissions]);
  const canManageRestrictions = useMemo(() => permissions.includes("manage_restrictions"), [permissions]);
  const canManageUsers = useMemo(() => permissions.includes("manage_users"), [permissions]);
  /**
   * `manage_users` used to gate org config and branding as well, which meant an
   * admin without user management could not reach their own organisation's
   * settings. These two split that apart: configuration (config, branding,
   * boundary, API keys) and bulk imports are now their own permissions.
   */
  const canManageConfiguration = useMemo(() => permissions.includes("manage_configuration"), [permissions]);
  const canBulkApiDataImport = useMemo(() => permissions.includes("bulk_api_data_import"), [permissions]);
  /**
   * Changing a style document: a CM table's `map_display_config.style`/`icons`, a
   * spatial dataset's `display_style`, the org boundary's style. The backend
   * compares rather than detects, so a save that sends the stored style back
   * unchanged still succeeds without it — only an actual change is refused.
   * The row style picker (`{ type: "camp" }`) is data entry and is NOT gated.
   */
  const canManageStyling = useMemo(() => permissions.includes("manage_styling"), [permissions]);

  /** Holds any management permission — i.e. can enter the Admin Console at all. */
  const canAdminister = useMemo(
    () => canManageUsers || canManageConfiguration || canManageRestrictions || canBulkApiDataImport,
    [canManageUsers, canManageConfiguration, canManageRestrictions, canBulkApiDataImport]
  );

  /** True for any user who can write (Editor or Admin) */
  const hasWritePermissions = useMemo(() => canCreate || canUpdate || canDelete, [canCreate, canUpdate, canDelete]);

  return {
    isAuthenticated,
    hasWritePermissions,
    canCreate,
    canUpdate,
    canDelete,
    canManageRestrictions,
    canManageUsers,
    canManageConfiguration,
    canBulkApiDataImport,
    canManageStyling,
    canAdminister
  } as const;
};

export default useAuth;
